/**
 * SongRegistryService — Firestore I/O for the canonical `songs` registry.
 *
 * Firestore Collection: songs
 * Structure:
 * {
 *   title: string,          // canonical display title
 *   titleLower: string,
 *   artist: string,         // reference artist for display/matching
 *   externalRefs: {         // EVIDENCE, never the identity (see songRegistryModel)
 *     musicbrainzWorkId?: string,
 *     musicbrainzRecordingId?: string,
 *     appleTrackId?: string,
 *   },
 *   createdAt, updatedAt: timestamp
 * }
 *
 * One doc per SONG. Repertoire rows point here via `canonicalSongId`
 * (null = unlinked). The doc id is the studio-owned canonical song id —
 * "every student singing this song" is the set of repertoire rows sharing it,
 * which is the join the Recital planning page needs. Merging two registry
 * docs is done by rewriting `canonicalSongId` on the affected repertoire
 * rows; nothing else in the system points at this collection.
 */

import {
	collection,
	doc,
	addDoc,
	updateDoc,
	getDocs,
	query,
	where,
	serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
	normalizeTitle,
	extractExternalRefs,
	artistMatches,
	buildSongDoc,
	refsBackfillPatch,
	REF_MATCH_ORDER,
} from './songRegistryModel';

const COLLECTION_NAME = 'songs';

const toRow = (snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() });

const findByExternalRef = async (key, value) => {
	const snapshot = await getDocs(
		query(collection(db, COLLECTION_NAME), where(`externalRefs.${key}`, '==', value))
	);
	return snapshot.empty ? null : toRow(snapshot.docs[0]);
};

/**
 * Resolve metadata to a canonical song id: match by external refs in
 * precedence order, then by title+artist, else mint a new registry doc.
 * Returns null when the metadata can't identify a song — the caller stores
 * that as an UNLINKED row for a human to link later, never a guess.
 *
 * Title+artist matching filters artist CLIENT-SIDE on purpose: the query
 * stays single-field (equality on titleLower), so it needs no composite
 * index — this codebase has shipped two index-less queries that 400'd only
 * in prod, and the registry is small enough that the filter costs nothing.
 *
 * Never throws: the registry is an enhancement to Add-a-song, not a gate on
 * it. A registry failure mid-lesson degrades to an unlinked row; it must not
 * block a teacher from adding a song.
 */
export const ensureCanonicalSong = async (metadata) => {
	try {
		if (!metadata?.title?.trim()) return null;
		const refs = extractExternalRefs(metadata);

		let match = null;
		for (const key of REF_MATCH_ORDER) {
			if (!refs[key]) continue;
			match = await findByExternalRef(key, refs[key]);
			if (match) break;
		}

		if (!match) {
			const snapshot = await getDocs(
				query(
					collection(db, COLLECTION_NAME),
					where('titleLower', '==', normalizeTitle(metadata.title))
				)
			);
			match =
				snapshot.docs
					.map(toRow)
					.find((song) => artistMatches(song.artist, metadata.artist)) || null;
		}

		if (match) {
			const patch = refsBackfillPatch(match.externalRefs, refs);
			if (patch) {
				await updateDoc(doc(db, COLLECTION_NAME, match.id), {
					...patch,
					updatedAt: serverTimestamp(),
				});
			}
			return match.id;
		}

		const ref = await addDoc(collection(db, COLLECTION_NAME), {
			...buildSongDoc(metadata),
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp(),
		});
		return ref.id;
	} catch (error) {
		console.error('ensureCanonicalSong failed; repertoire row will be unlinked:', error);
		return null;
	}
};

/**
 * Every registry song, for the pickers that let a human choose one (the Recital
 * set list builder today).
 *
 * An ALL-FETCH with client-side filtering, deliberately, and the same call the
 * Calendar makes against `events`: the registry is one doc per song the studio
 * has ever worked — 7 in production, a few hundred at maturity — so paging it is
 * cost no one is paying. It also keeps the promise the resolve path makes: every
 * query against `songs` stays single-field, so this collection never acquires a
 * composite index that a later `orderBy` could silently outgrow (two index-less
 * queries 400'd in prod this week; none of them were here).
 *
 * Sorted by title client-side for the same reason.
 */
export const fetchAllSongs = async () => {
	const snapshot = await getDocs(collection(db, COLLECTION_NAME));
	return snapshot.docs
		.map(toRow)
		.sort((a, b) => (a.titleLower || '').localeCompare(b.titleLower || ''));
};

const SongRegistryService = { ensureCanonicalSong, fetchAllSongs };
export default SongRegistryService;
